import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { activityService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Card, Badge, Input, EmptyState, Spinner, Button, StatCard, HorizontalBarChart, TrendChart, Pagination } from '../../components/ui';

// Same shared-look tab pill used in ModuleDetail.js's tab bar - a solid
// primary pill on the active tab rather than an underline.
function TabButton({ active, onClick, label, icon }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition whitespace-nowrap text-sm ${
        active ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:bg-surface-100'
      }`}
    >
      <span className="w-[18px] h-[18px] shrink-0">{icon}</span>
      {label}
    </button>
  );
}

function DeviceIcon({ type }) {
  if (type === 'mobile') {
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  }
  if (type === 'tablet') {
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 17h.01M7 21h10a1 1 0 001-1V4a1 1 0 00-1-1H7a1 1 0 00-1 1v16a1 1 0 001 1z" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function SessionsTab({ t, addNotification, confirm }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlineOnly, setOnlineOnly] = useState(true);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const pageSize = 20;

  const fetchSessions = useCallback(() => {
    setLoading(true);
    activityService.getSessions({ page, online_only: onlineOnly ? 'true' : undefined })
      .then((res) => {
        setSessions(res.data.results || []);
        setCount(res.data.count || 0);
      })
      .catch(() => addNotification(t('Failed to load sessions'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, onlineOnly]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);
  useEffect(() => { setPage(1); }, [onlineOnly]);

  const handleTerminate = async (session) => {
    const name = `${session.user.first_name} ${session.user.last_name}`;
    if (!(await confirm(t('End {{name}}\'s session? They\'ll be signed out next time their app tries to refresh.', { name })))) return;
    try {
      await activityService.terminateSession(session.id);
      addNotification(t('Session ended'), 'success');
      fetchSessions();
    } catch (error) {
      addNotification(t('Failed to end session'), 'error');
    }
  };

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={onlineOnly} onChange={(e) => setOnlineOnly(e.target.checked)} className="rounded" />
          {t('Show only currently connected')}
        </label>
      </div>

      {loading ? (
        <div className="p-12 flex justify-center"><Spinner /></div>
      ) : sessions.length === 0 ? (
        <EmptyState title={t('No sessions found')} description={t('Logins will appear here.')} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">{t('User')}</th>
                <th className="text-left px-4 py-3">{t('IP Address')}</th>
                <th className="text-left px-4 py-3">{t('Device')}</th>
                <th className="text-left px-4 py-3">{t('Signed in')}</th>
                <th className="text-left px-4 py-3">{t('Last seen')}</th>
                <th className="text-left px-4 py-3">{t('Status')}</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sessions.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{s.user.first_name} {s.user.last_name}</div>
                    <div className="text-xs text-gray-500">{s.user.email} · {s.user.role}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 tabular-nums">{s.ip_address || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    <span className="inline-flex items-center gap-1.5">
                      <DeviceIcon type={s.device_type} />
                      {s.browser} · {s.os}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{new Date(s.last_seen_at).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    {s.ended_at ? (
                      <Badge tone="gray">{t('Ended')}</Badge>
                    ) : s.is_online ? (
                      <Badge tone="green">{t('Online')}</Badge>
                    ) : (
                      <Badge tone="yellow">{t('Idle')}</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!s.ended_at && (
                      <button
                        onClick={() => handleTerminate(s)}
                        className="text-xs font-medium text-red-600 hover:underline"
                      >
                        {t('End session')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={page} count={count} pageSize={pageSize} onPageChange={setPage} />
    </Card>
  );
}

const METHOD_TONE = { POST: 'green', PATCH: 'blue', PUT: 'blue', DELETE: 'red' };

function LogsTab({ t, addNotification }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const pageSize = 50;

  const fetchLogs = useCallback(() => {
    setLoading(true);
    activityService.getLogs({ page, page_size: pageSize, user: search || undefined })
      .then((res) => {
        setEntries(res.data.results || []);
        setCount(res.data.count || 0);
      })
      .catch(() => addNotification(t('Failed to load activity log'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  useEffect(() => {
    const id = setTimeout(fetchLogs, 300);
    return () => clearTimeout(id);
  }, [fetchLogs]);
  useEffect(() => { setPage(1); }, [search]);

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <Input
          placeholder={t('Filter by user email...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:max-w-sm"
        />
      </div>

      {loading ? (
        <div className="p-12 flex justify-center"><Spinner /></div>
      ) : entries.length === 0 ? (
        <EmptyState title={t('No activity recorded yet')} description={t('Actions like creating, editing, or deleting something will appear here.')} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">{t('When')}</th>
                <th className="text-left px-4 py-3">{t('User')}</th>
                <th className="text-left px-4 py-3">{t('Action')}</th>
                <th className="text-left px-4 py-3">{t('Path')}</th>
                <th className="text-left px-4 py-3">{t('IP')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entries.map((e, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</td>
                  <td className="px-4 py-3 text-gray-700">{e.email || t('System')}</td>
                  <td className="px-4 py-3">
                    <Badge tone={METHOD_TONE[e.method] || 'gray'}>{e.method}</Badge>
                    {e.status_code >= 400 && <span className="ml-2 text-xs text-red-500">{e.status_code}</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs">{e.path}</td>
                  <td className="px-4 py-3 text-gray-500 tabular-nums">{e.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={page} count={count} pageSize={pageSize} onPageChange={setPage} />
    </Card>
  );
}

function SettingsTab({ t, addNotification }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    activityService.getSettings().then((res) => setForm(res.data)).catch(() => {});
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await activityService.updateSettings({
        log_retention_days: parseInt(form.log_retention_days, 10),
        online_threshold_minutes: parseInt(form.online_threshold_minutes, 10),
      });
      setForm(res.data);
      addNotification(t('Settings saved'), 'success');
    } catch (error) {
      addNotification(t('Failed to save settings'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Card><div className="flex justify-center p-8"><Spinner /></div></Card>;

  return (
    <Card>
      <form onSubmit={handleSave} className="space-y-5 max-w-md">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('Log retention (days)')}</label>
          <input
            type="number"
            min={1}
            max={365}
            value={form.log_retention_days}
            onChange={(e) => setForm((p) => ({ ...p, log_retention_days: e.target.value }))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <p className="mt-1 text-xs text-gray-500">{t('Rotated activity-log files older than this are deleted automatically, about once an hour.')}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('"Online" threshold (minutes)')}</label>
          <input
            type="number"
            min={1}
            max={120}
            value={form.online_threshold_minutes}
            onChange={(e) => setForm((p) => ({ ...p, online_threshold_minutes: e.target.value }))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <p className="mt-1 text-xs text-gray-500">{t('A session counts as "currently connected" if it was active within this window.')}</p>
        </div>
        <Button type="submit" loading={saving}>{t('Save Changes')}</Button>
      </form>
    </Card>
  );
}

function AdminConnections() {
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('sessions');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    activityService.getStats().then((res) => setStats(res.data)).catch(() => {});
  }, []);

  const s = stats || {};
  const byRoleBars = Object.entries(s.by_role || {}).map(([label, value]) => ({ label, value }));
  const byBrowserBars = Object.entries(s.by_browser || {}).map(([label, value]) => ({ label, value }));

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Connections & Logs')} subtitle={t('Who is connected, from where, and what they did')} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <StatCard
              label={t('Connected Now')}
              value={s.connected_now ?? '—'}
              color="green"
              icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
            />
            <StatCard
              label={t('Sessions Today')}
              value={s.sessions_today ?? '—'}
              color="blue"
              icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>}
            />
            <StatCard
              label={t('Unique IPs Today')}
              value={s.unique_ips_today ?? '—'}
              color="purple"
              icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            />
            <StatCard
              label={t('Log Retention')}
              value={t('{{count}}d', { count: 7 })}
              color="yellow"
              onClick={() => setActiveTab('settings')}
              icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
            />
          </div>

          {stats && (s.hourly_logins_24h?.length > 0 || byRoleBars.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-2xl shadow-card p-4">
                <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Logins (last 24h)')}</h3>
                <TrendChart points={s.hourly_logins_24h || []} color="#6739e0" valueSuffix="" emptyLabel={t('No logins yet')} />
              </div>
              <div className="bg-white rounded-2xl shadow-card p-4">
                <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Connected Now by Role')}</h3>
                <HorizontalBarChart bars={byRoleBars} valueSuffix="" emptyLabel={t('Nobody connected right now')} />
              </div>
              {byBrowserBars.length > 0 && (
                <div className="bg-white rounded-2xl shadow-card p-4 lg:col-span-2">
                  <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Sessions Today by Browser')}</h3>
                  <HorizontalBarChart bars={byBrowserBars} valueSuffix="" emptyLabel={t('No sessions today')} />
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-card mb-6">
            <div className="flex gap-1 p-2 border-b border-surface-100 overflow-x-auto">
              <TabButton
                active={activeTab === 'sessions'}
                onClick={() => setActiveTab('sessions')}
                label={t('Live Sessions')}
                icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>}
              />
              <TabButton
                active={activeTab === 'logs'}
                onClick={() => setActiveTab('logs')}
                label={t('Activity Log')}
                icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
              />
              <TabButton
                active={activeTab === 'settings'}
                onClick={() => setActiveTab('settings')}
                label={t('Settings')}
                icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
              />
            </div>
          </div>

          {activeTab === 'sessions' && <SessionsTab t={t} addNotification={addNotification} confirm={confirm} />}
          {activeTab === 'logs' && <LogsTab t={t} addNotification={addNotification} />}
          {activeTab === 'settings' && <SettingsTab t={t} addNotification={addNotification} />}
        </main>
      </div>
    </div>
  );
}

export default AdminConnections;
